// @ts-nocheck
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useTeamRole } from '@/hooks/useTeamRole';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip';
import { MoreVertical } from 'lucide-react';
import TeamRosterView from '@/components/teams/TeamRosterView';
import TeamScheduledEventsView from '@/components/teams/TeamScheduledEventsView';
import PlayerEditForm from '@/components/rosters/PlayerEditForm';
import { MissingInfoIcons, ClaimControls } from '@/components/rosters/PlayerStatus';
import { Pencil } from 'lucide-react';
import TeamRegistrationsList from '@/components/teams/TeamRegistrationsList';
import TeamBillingView from '@/components/teams/TeamBillingView';
import TeamMembersList from '@/components/teams/TeamMembersList';
import TeamSettingsForm from '@/components/teams/TeamSettingsForm';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

const ownershipBadge: Record<string, { label: string; cls: string }> = {
  unclaimed: { label: 'Unclaimed', cls: 'bg-muted text-muted-foreground' },
  pending_coach_claim: { label: 'Pending coach claim', cls: 'bg-amber-100 text-amber-800' },
  claimed: { label: 'Verified', cls: 'bg-green-100 text-green-800' },
};

export default function TeamDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'overview');
  const [minDelay, setMinDelay] = useState(true);
  const [presetRegistrationId, setPresetRegistrationId] = useState<string | null>(null);
  const [rosterAutoAdd, setRosterAutoAdd] = useState(false);

  const openAddPlayerFlow = () => {
    setRosterAutoAdd(true);
    setTab('roster');
  };

  useEffect(() => {
    const t = setTimeout(() => setMinDelay(false), 500);
    return () => clearTimeout(t);
  }, []);

  const { data: team, isLoading, error, refetch } = useQuery({
    queryKey: ['team', slug],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('teams')
        .select('*, club:clubs(*)')
        .eq('stable_slug', slug)
        .maybeSingle();
      if (error) throw error;
      if (!data) throw new Error('Team not found');
      return data;
    },
  });

  const { data: role } = useTeamRole(team?.id);

  const { data: recentEvents = [] } = useQuery({
    queryKey: ['team-recent-events', team?.id],
    enabled: !!team,
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from('v_team_registrations_summary')
        .select('*')
        .eq('team_id', team.id)
        .in('registration_status', ['completed', 'checked_in'])
        .order('event_start_date', { ascending: false })
        .limit(3);
      return data ?? [];
    },
  });

  const { data: upcomingEvents = [] } = useQuery({
    queryKey: ['team-upcoming-events', team?.id],
    enabled: !!team,
    queryFn: async () => {
      const today = new Date().toISOString().split('T')[0];
      const { data } = await (supabase as any)
        .from('v_team_registrations_summary')
        .select('*')
        .eq('team_id', team.id)
        .in('registration_status', ['registered', 'checked_in', 'waitlisted'])
        .gte('event_start_date', today)
        .order('event_start_date', { ascending: true })
        .limit(3);
      return data ?? [];
    },
  });

  const { data: rosterData } = useQuery({
    queryKey: ['team-current-roster', team?.id],
    enabled: !!team,
    queryFn: async () => {
      const res = await invokeEdgeFunction('rle-list-team-roster', {
        body: { team_id: team.id },
      });
      if (res.error) throw new Error(res.error.message ?? 'Request failed');
      const payload = res.data?.data ?? res.data;
      return {
        master: payload?.master ?? [],
        guests: payload?.guests ?? [],
        masterCount: payload?.master_count ?? (payload?.master ?? []).length,
      };
    },
  });

  const currentRoster = (rosterData?.master ?? []).slice(0, 6);
  const currentGuests = (rosterData?.guests ?? []).slice(0, 6);
  const masterCount = rosterData?.masterCount ?? currentRoster.length;

  if (isLoading || minDelay) {
    return (
      <div className="container mx-auto py-8 space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="container mx-auto py-16 flex justify-center">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center space-y-4">
            <h2 className="text-xl font-semibold">Team not found</h2>
            <Button onClick={() => navigate('/')}>Back to home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const subtitleParts = [team.age_group, team.gender, team.home_state].filter(Boolean);
  const ob = ownershipBadge[team.ownership_status] ?? ownershipBadge.unclaimed;

  return (
    <div className="container mx-auto py-8 space-y-6 max-w-6xl">
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              {team.logo_url ? (
                <img src={team.logo_url} alt={team.canonical_name} className="h-20 w-20 rounded-full object-cover" />
              ) : (
                <div
                  className="h-20 w-20 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: team.primary_color ?? '#f97316' }}
                >
                  {(team.canonical_name ?? '?')[0]?.toUpperCase()}
                </div>
              )}
              <div>
                <h1 className="text-[22px] font-semibold">{team.display_name ?? team.canonical_name}</h1>
                {subtitleParts.length > 0 && (
                  <p className="text-sm text-muted-foreground">{subtitleParts.join(' · ')}</p>
                )}
                {team.club && (
                  <Link to={`/clubs/${team.club.slug}`} className="text-sm text-muted-foreground hover:underline">
                    {team.club.name}
                  </Link>
                )}
                <p className="text-xs text-muted-foreground mt-1">Team ID: {team.id?.slice(0, 8)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className={`inline-block px-2 py-1 rounded text-xs ${ob.cls}`}>{ob.label}</span>
              {role?.isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => setTab('members')}>Invite member</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setTab('settings')}>Edit team settings</DropdownMenuItem>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <DropdownMenuItem disabled>Transfer ownership</DropdownMenuItem>
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>Feature coming soon</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="roster">Team roster</TabsTrigger>
          <TabsTrigger value="events">Scheduled events</TabsTrigger>
          <TabsTrigger value="registrations">Registrations</TabsTrigger>
          {role?.canViewBilling && <TabsTrigger value="billing">Billing</TabsTrigger>}
          <TabsTrigger value="members">Members</TabsTrigger>
          {role?.isAdmin && <TabsTrigger value="settings">Settings</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Recent events</CardTitle></CardHeader>
              <CardContent>
                {recentEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No past events yet.</p>
                ) : (
                  <ul className="space-y-2">
                    {recentEvents.map((e: any) => (
                      <li key={e.event_registration_id} className="flex justify-between text-sm">
                        <Link to={`/events/${e.event_slug}`} className="hover:underline">{e.event_name}</Link>
                        <span className="text-muted-foreground">{e.event_start_date}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader><CardTitle className="text-base">Upcoming events</CardTitle></CardHeader>
              <CardContent>
                {upcomingEvents.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No upcoming events. <Link to="/events" className="text-primary hover:underline">Register for an event →</Link>
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {upcomingEvents.map((e: any) => (
                      <li key={e.event_registration_id} className="flex justify-between text-sm">
                        <Link to={`/events/${e.event_slug}`} className="hover:underline">{e.event_name}</Link>
                        <span className="text-muted-foreground">{e.event_start_date}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">Current roster ({masterCount})</CardTitle></CardHeader>
            <CardContent>
              {currentRoster.length === 0 && currentGuests.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No current roster.{' '}
                  <button className="text-primary hover:underline" onClick={openAddPlayerFlow}>
                    Add players →
                  </button>
                </p>
              ) : (
                <>
                  {(() => {
                    const renderItem = (p: any, isGuest = false) => {
                      const name = p.full_name ?? p.name ?? '—';
                      if (editingId === p.player_id) {
                        return (
                          <li key={p.player_id ?? p.id} className="py-2">
                            <PlayerEditForm
                              player={p}
                              teamId={team.id}
                              onCancel={() => setEditingId(null)}
                              onSaved={() => {
                                setEditingId(null);
                                queryClient.invalidateQueries({ queryKey: ['team-current-roster', team.id] });
                              }}
                            />
                          </li>
                        );
                      }
                      return (
                        <li key={p.player_id ?? p.id} className="flex items-center gap-2 text-sm bg-white even:bg-slate-50 hover:bg-slate-100 py-1.5 px-2">
                          <span className="font-medium">{name}</span>
                          {isGuest && (
                            <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200">Guest</Badge>
                          )}
                          {p.position ? <span className="text-muted-foreground">· {p.position}</span> : null}
                          {isGuest && p.home_team_name && (
                            <span className="text-xs text-muted-foreground">from {p.home_team_name}</span>
                          )}
                          <MissingInfoIcons missing={p.missing} />
                          <div className="ml-auto flex items-center gap-2">
                            <ClaimControls
                              player={p}
                              onChanged={() => queryClient.invalidateQueries({ queryKey: ['team-current-roster', team.id] })}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => setEditingId(p.player_id)}
                              aria-label={`Edit ${name}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </li>
                      );
                    };
                    return (
                      <TooltipProvider>
                        {currentRoster.length > 0 && (
                          <ul className="divide-y rounded-md overflow-hidden">{currentRoster.map((p: any) => renderItem(p))}</ul>
                        )}
                        {currentGuests.length > 0 && (
                          <div className="mt-4">
                            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Guests</p>
                            <ul className="divide-y rounded-md overflow-hidden">{currentGuests.map((p: any) => renderItem(p, true))}</ul>
                          </div>
                        )}
                      </TooltipProvider>
                    );
                  })()}
                  <button className="mt-4 text-sm text-primary hover:underline" onClick={() => setTab('roster')}>
                    View full roster →
                  </button>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="roster">
          <TeamRosterView team={team} autoOpenAdd={rosterAutoAdd} key={presetRegistrationId ?? 'default'} />
        </TabsContent>

        <TabsContent value="events">
          <TeamScheduledEventsView team={team} />
        </TabsContent>


        <TabsContent value="registrations">
          <TeamRegistrationsList
            team={team}
            onViewRoster={(rid) => {
              setPresetRegistrationId(rid);
              setTab('roster');
            }}
          />
        </TabsContent>

        {role?.canViewBilling && (
          <TabsContent value="billing">
            <TeamBillingView team={team} />
          </TabsContent>
        )}

        <TabsContent value="members">
          <TeamMembersList team={team} />
        </TabsContent>

        {role?.isAdmin && (
          <TabsContent value="settings">
            <TeamSettingsForm team={team} onUpdated={() => refetch()} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
